import { ButtonBase, styled } from '@mui/material';
import { monthToString } from 'utils/utils';
import { calendarLayout } from './constants';
import { Month } from './utils';

const SelectorLayout = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `${calendarLayout.selectorYearColumnWidth}px 1fr`,
    gap: theme.spacing(calendarLayout.sectionGap),
    minHeight: calendarLayout.selectorMinHeight,
}));

const SelectorColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(calendarLayout.compactGap),
    maxHeight: calendarLayout.selectorMinHeight,
    overflowY: 'auto',
    paddingRight: theme.spacing(calendarLayout.compactGap),
}));

const YearButton = styled(ButtonBase, {
    shouldForwardProp: prop => prop !== '$selected',
})<{
    $selected: boolean;
}>(({ theme, $selected }) => ({
    ...theme.typography.body2,
    'display': 'flex',
    'justifyContent': 'center',
    'width': '100%',
    'padding': theme.spacing(0.75, 1),
    'borderRadius': theme.shape.borderRadius,
    'color': theme.palette.text.primary,
    'backgroundColor': $selected ? theme.palette.primary[50] : 'transparent',
    'fontWeight': $selected ? 600 : 400,
    '&:hover': {
        backgroundColor: $selected
            ? theme.palette.primary[50]
            : theme.palette.grey[100],
    },
}));

const MonthGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(calendarLayout.sectionGap),
    alignContent: 'start',
}));

const SelectorMonthButton = styled(ButtonBase, {
    shouldForwardProp: prop =>
        !['$selected', '$disabled'].includes(prop as string),
})<{
    $selected: boolean;
    $disabled: boolean;
}>(({ theme, $selected, $disabled }) => ({
    ...theme.typography.body2,
    'display': 'flex',
    'justifyContent': 'center',
    'alignItems': 'center',
    'minHeight': calendarLayout.selectorMonthMinHeight,
    'padding': theme.spacing(0.75, 1),
    'borderRadius': theme.shape.borderRadius,
    'color': $disabled
        ? theme.palette.text.disabled
        : theme.palette.text.primary,
    'backgroundColor': $selected ? theme.palette.primary[100] : 'transparent',
    'cursor': $disabled ? 'default' : 'pointer',
    'opacity': $disabled ? 0.5 : 1,
    '&:hover': {
        backgroundColor: $disabled
            ? 'transparent'
            : $selected
              ? theme.palette.primary[100]
              : theme.palette.grey[100],
    },
}));

interface CalendarMonthSelectorProps {
    years: number[];
    selectorYear: number;
    month: Month;
    isMonthSelectable: (month: number) => boolean;
    onSelectYear: (year: number) => void;
    onSelectMonth: (month: number) => void;
    setYearButtonRef: (year: number, element: HTMLButtonElement | null) => void;
}

export const CalendarMonthSelector = ({
    years,
    selectorYear,
    month,
    isMonthSelectable,
    onSelectYear,
    onSelectMonth,
    setYearButtonRef,
}: CalendarMonthSelectorProps) => {
    return (
        <SelectorLayout>
            <SelectorColumn>
                {years.map(year => (
                    <YearButton
                        key={year}
                        $selected={year === selectorYear}
                        ref={element => {
                            setYearButtonRef(year, element);
                        }}
                        onClick={() => onSelectYear(year)}>
                        {year}
                    </YearButton>
                ))}
            </SelectorColumn>
            <MonthGrid>
                {Array.from({ length: 12 }, (_, monthIndex) => {
                    const disabled = !isMonthSelectable(monthIndex);
                    const isSelected =
                        selectorYear === month.year &&
                        monthIndex === month.month;

                    return (
                        <SelectorMonthButton
                            key={`${selectorYear}-${monthIndex}`}
                            $selected={isSelected}
                            $disabled={disabled}
                            onClick={() => {
                                if (!disabled) {
                                    onSelectMonth(monthIndex);
                                }
                            }}>
                            {monthToString(monthIndex + 1, true)}
                        </SelectorMonthButton>
                    );
                })}
            </MonthGrid>
        </SelectorLayout>
    );
};

export default CalendarMonthSelector;
